const IMAGE_TYPE_RE = /^image\/(jpeg|jpg|png|gif|webp)$/i;

export function isAllowedCoverImageType(type) {
  return IMAGE_TYPE_RE.test(String(type ?? ''));
}

export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('IMAGE_LOAD_FAILED'));
    };
    img.src = objectUrl;
  });
}

export function resolveOutputMime(sourceType) {
  const type = String(sourceType ?? '').toLowerCase();
  if (type === 'image/png' || type === 'image/gif') {
    return 'image/png';
  }
  return 'image/jpeg';
}

function resolveOutputExtension(mime) {
  return mime === 'image/png' ? 'png' : 'jpg';
}

export function canvasToBlob(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('IMAGE_COMPRESS_FAILED'));
          return;
        }
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

export function buildProcessedFile(blob, file, mime) {
  const ext = resolveOutputExtension(mime);
  const baseName = String(file.name ?? 'cover').replace(/\.[^.]+$/, '') || 'cover';
  return new File([blob], `${baseName}.${ext}`, { type: mime, lastModified: Date.now() });
}

/**
 * 按创建市场规则处理封面图：可选正方形校验 + 压缩至 maxDimension。
 * 未传 requireSquare / maxDimension 时原样返回。
 *
 * @param {File} file
 * @param {{ requireSquare?: boolean, maxDimension?: number }} [options]
 * @returns {Promise<File>}
 */
export async function processCoverImage(file, options = {}) {
  const requireSquare = Boolean(options.requireSquare);
  const maxDimension = Number(options.maxDimension);

  if (!requireSquare && !(Number.isFinite(maxDimension) && maxDimension > 0)) {
    return file;
  }

  const img = await loadImageFromFile(file);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;

  if (!width || !height) {
    throw new Error('IMAGE_LOAD_FAILED');
  }
  if (requireSquare && width !== height) {
    throw new Error('NOT_SQUARE');
  }

  if (!Number.isFinite(maxDimension) || maxDimension <= 0) {
    return file;
  }

  let outputWidth = width;
  let outputHeight = height;

  if (requireSquare) {
    const outputSize = Math.min(width, maxDimension);
    outputWidth = outputSize;
    outputHeight = outputSize;
  } else {
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    outputWidth = Math.max(1, Math.round(width * scale));
    outputHeight = Math.max(1, Math.round(height * scale));
  }

  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('IMAGE_COMPRESS_FAILED');
  }

  ctx.drawImage(img, 0, 0, outputWidth, outputHeight);

  const mime = resolveOutputMime(file.type);
  const quality = mime === 'image/jpeg' ? 0.85 : undefined;
  const blob = await canvasToBlob(canvas, mime, quality);
  return buildProcessedFile(blob, file, mime);
}
